/**
 * Cross-surface helpers for the document-review reimbursement views.
 *
 * Module convention: anything that needs to be referenced by BOTH
 * `DocumentReviewTab.tsx` (flat Reimbursements tab) and
 * `ExpandedRecordDetails.tsx` (Claimants-tab nested reimbursements panel)
 * lives here as the single source of truth — config, predicates, render
 * shells, and any future cross-surface concern.
 *
 * Motivated by a past predicate-drift incident: three near-identical copies
 * of the survivor-filter predicate had diverged between those two surfaces
 * and produced an expand-badge-vs-expanded-table count mismatch. Anything
 * cross-surface goes here on first introduction; do not duplicate it into
 * either consumer file.
 */

import { Box } from "@mui/material";
import { ReactNode } from "react";

import { ExtractedRecord, ReimbursementRowForecast, TargetSchema } from "@/types";

import { absoluteIndexOf } from "../../utils/recordIndexing";

import { ReimbursementLineItemsTable } from "./ReimbursementLineItemsTable";

/**
 * Builds a survivor-filter closure for reimbursement-schema records.
 *
 * Used in three sibling sites — the flat Reimbursements tab grid, the
 * Claimants-tab nested expand, and the expand-badge count — that previously
 * each maintained their own copy of the predicate. Those copies had drifted
 * and produced a count mismatch between the expand-chevron badge and the
 * expanded table on the Reimbursements panel.
 *
 * Records without a reimbursement-typed field pass through unconditionally,
 * regardless of `keepUnknown` — the mixed-schema linked-record call sites
 * rely on this so claim_items and other nested-schema rows aren't filtered
 * by the reimbursement survivor set.
 *
 * Records with no known index pass through by default (`keepUnknown: true`,
 * defensive — defer to the caller's input rather than silently dropping).
 * Callers operating on a homogeneous reimbursement-schema list pass
 * `keepUnknown: false` for strict-drop semantics; this relies on every input
 * record carrying a reimbursement-typed field, currently guaranteed by the
 * schema bucketing in `useReviewRecords` (via `getRecordSchemas`). If that
 * bucketing invariant breaks, the strict-drop semantics this caller expects
 * no longer hold.
 *
 * Caller is still responsible for the projection-availability gate.
 */
export const createReimbursementSurvivorFilter = (survivorIndices: Set<number>) => {
  return (records: ExtractedRecord[], { keepUnknown = true }: { keepUnknown?: boolean } = {}): ExtractedRecord[] => {
    return records.filter((r) => {
      // Absolute index carried on the record (#6755), not its position in
      // `records` — which here is already a filtered, per-schema array.
      const idx = absoluteIndexOf(r);
      if (idx === undefined) return keepUnknown;
      const hasReimbursementFields = (r.extracted_fields ?? []).some(
        (f) => f.target_schema === TargetSchema.REIMBURSEMENT
      );
      if (!hasReimbursementFields) return true;
      return survivorIndices.has(idx);
    });
  };
};

/**
 * Renders the line-items expand panel for a reimbursement row, or null when
 * the forecast carries no line items. Centralizes the wrapper so the flat
 * Reimbursements tab and the Claimants-tab nested reimbursements panel render
 * the same shell — preventing them from drifting if either is restyled.
 */
export const renderLineItemsExpand = (forecast: ReimbursementRowForecast | undefined): ReactNode => {
  if (!forecast?.line_items || forecast.line_items.length === 0) return null;
  return (
    <Box sx={{ pl: 2, py: 1 }}>
      <ReimbursementLineItemsTable lineItems={forecast.line_items} />
    </Box>
  );
};
